import tenantConstants from '@constants';
import tenantPayloads from '@payloads';
import tenantTransformers from '@transformers';


import { getAPIBaseURL, getClassifiedBaseURL } from '../../../utility/env';
import { convertQueryObjToString } from '../../../utility/urlQuery';

const userApiEndpoints = {
  getProfileDetailsEndpoint: (userId) => {
    return {
      url: `/api/users/${userId}`,
      transformer: (response) => tenantTransformers.profileDataMapper(response?.user),
    };
  },

  updateProfileDetailsEndpoint: (data) => {
    return {
      url: `/api/users/${data?.userId}`,
      method: 'PUT',
      body: tenantPayloads.updateProfilePayload(data?.body),
      transformer: (response) => tenantTransformers.profileDataMapper(response?.user),
    };
  },

  getAutoGeneratedUserDescription: {
    query: (data) =>
      `/api/users/generate_description?${convertQueryObjToString(tenantPayloads.autoGenerateUserDescriptionPayload(data))}`,
  },

  getUserActiveLicenses: {
    query: ({ id, type }) => {
      return {
        url: type === 'Agency' ? `api/agencies/${id}/licenses` : `/api/users/${id}/licenses`,
      };
    },
  },

  getUsersSavedSearches: {
    query: ({ locale }) => {
      return {
        url: `${getClassifiedBaseURL()}/api/user/searches/saved/`,
        method: 'GET',
        headers: {
          'Accept-Language': locale || '',
        },
      };
    },
  },

  deleteSavedSearch: {
    query: ({ locale, userID, id }) => {
      return {
        url: `${getClassifiedBaseURL()}/api/user/${userID}/searches/saved/${id}`,
        method: 'DELETE',
        headers: {
          'Accept-Language': locale || '',
        },
      };
    },
  },

  getFavouriteProperties: {
    query: () => {
      return {
        url: `${getClassifiedBaseURL()}/api/user/favorites`,
        method: 'GET',
      };
    },
  },

  saveSettings: {
    query: ({ payload }) => {
      return {
        url: `${getClassifiedBaseURL()}/api/saveSettings`,
        method: 'POST',
        body: payload,
      };
    },
  },

  updateLicense: {
    query: ({ licenseId, values }) => {
      return {
        url: `api/licenses/${licenseId}`,
        method: 'PATCH',
        body: values,
      };
    },
  },

  updateUserPreferences: (userId) => {
    return {
      url: `/api/users/${userId}/toggle_auto_utilization`,
      method: 'PATCH',
    };
  },

  updatePushNotifications: ({ userId, payload }) => {
    return {
      url: `api/users/${userId}/preferences`,
      method: 'POST',
      body: payload,
    };
  },

  changePassword: {
    query: ({ userId, values, auth }) => {
      return {
        url: `/api/users/${userId}/change_password`,
        method: 'POST',
        body: {
          user: {
            current_password: values?.oldPassword,
            new_password: values?.newPassword,
            new_password_confirmation: values?.confirmPassword,
          },
        },
        headers: {},
      };
    },
    transformer: (res) => {
      return res?.data || { message: 'Password Updated Successfully' };
    },
  },

  userPreference: {
    query: ({ id, values }) => {
      return {
        url: `/api/users/${id}/preferences`,
        method: 'POST',
        body: {
          preferences: {
            email_notification: values?.email_notification,
            newsletter: values?.newsletter,
            automated_reports: values?.automated_reports,
            currency: values?.currency,
            area_unit: values?.area_unit,
          },
        },
      };
    },
  },

  getUserSettingsDetail: {
    query: ({ id }) => {
      return { url: `/api/users/${id}` };
    },
    transformer: (response) => tenantTransformers.settingsDetailDataMapper(response),
  },

  getUserOtp: {
    query: (params) => {
      return `api/users/${params?.id}/otp?mobile_number=${params?.val}`;
    },
  },

  verifyUserOtp: {
    query: (params) => {
      return { url: `api/users/${params?.id}/verify_mobile_number`, method: 'PATCH', body: params?.values };
    },
  },

  getTruPointsCriteriaData: {
    query: () => {
      return { url: `/api/tru_points` };
    },
  },

  getTruPointsActivityLogsData: {
    query: ({ page, userId, per_page }) => {
      const pageParam = page || 1;
      const perPageParam = per_page || 10;
      return { url: `/api/users/${userId}/trubroker/tru_point_activities?page=${pageParam}&per_page=${perPageParam}` };
    },
  },
  getUserNotificationsData: {
    query: ({ page, userId }) => {
      return { url: `api/notifications?page=${page || 1}&q[user_id_eq]=${userId}` };
    },
  },

  getUserNotificationsCount: {
    query: () => {
      return { url: `/api/notifications/stats` };
    },
  },

  markAsRead: {
    query: (data) => ({
      url: `api/notifications/${data?.notificationId}/mark_as_read`,
      method: 'PUT',
      body: data,
    }),
  },
  markAllAsRead: {
    query: () => ({
      url: `api/notifications/mark_all_as_read`,
      method: 'PUT',
    }),
  },

  getTruBrokerLeaderboardData: {
    query: ({ page = 1, per_page = 10 } = {}) => {
      return { url: `/api/users/trubroker/leader_board?page=${page}&per_page=${per_page}` };
    },
    transformer: (response, meta, args) => {
      return tenantTransformers.truBrokerLeaderboardMapper(response);
    },
  },

  getNafathVerificationUrl: {
    query: (params) => {
      return `${getAPIBaseURL()}/api/ksa_verifications/nafaz_auth?${convertQueryObjToString(params)}`;
    },
  },
};

export default userApiEndpoints;
